// 压缩件确认：超过门户大小上限的文件后台压了一份上传副本，上传政府用它。左右对比原件和压缩后的，人看过才能开始任务：
// 看得清 → 确认；看不清 → 换一份文件（传到同一类别，替掉这份，重新压）；还不行 → 叫管理员（WhatsApp）
import { useState } from "react";
import { createPortal } from "react-dom";
import type { TaskFileDTO } from "@csp/shared";
import { api, errorText } from "../lib/api";
import { mediaUrl } from "../lib/net";
import { uploadTaskFile } from "../lib/upload";
import { FileView } from "./FilePreview";

const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);

export function CopyCheckDialog({ taskId, file, token, onChanged, onClose }: {
  taskId: string; file: TaskFileDTO; token: string | null; onChanged: () => void; onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const name = file.name.split("／").pop() ?? file.name;
  const copy = file.uploadCopy;
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const confirm = () => run(async () => {
    await api.confirmUploadCopy(file.id);
    onChanged();
    onClose();
  });
  const replace = (picked: File | undefined) => picked && run(async () => {
    await uploadTaskFile(taskId, file.key, picked);
    // 能放多份的类别不会自动替掉旧的：删掉这份
    await api.removeFile(file.id).catch(() => undefined);
    onChanged();
    onClose();
  });
  const help = () => run(async () => {
    await api.fileHelp(file.id);
    setMsg("已通知管理员（WhatsApp），等管理员处理。");
  });

  return createPortal(
    <div className="modal-mask modal-top" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal copy-check" role="dialog" aria-modal="true" aria-label={`确认压缩后的 ${name}`} data-testid="copy-check">
        <h2 translate="no">确认压缩后的「{name}」看得清</h2>
        <p className="small muted">
          {file.sizeIssue
            ? file.sizeIssue
            : <>原件 {kb(file.size)} 超过政府网站上限，已压到 {copy ? kb(copy.size) : "—"}（{copy?.how}），上传时用压缩后的。请对比：文字、印章、照片都要看得清。</>}
        </p>
        <div className="copy-check-panes">
          <section>
            <h3>原件 {kb(file.size)}</h3>
            <FileView target={{ url: mediaUrl(`/api/files/${file.id}`, token), name: file.name, contentType: file.contentType }} />
          </section>
          {copy && (
            <section>
              <h3>压缩后 {kb(copy.size)}（上传用这份）</h3>
              <FileView target={{ url: mediaUrl(`/api/files/${file.id}/upload-copy`, token), name: "" }} />
            </section>
          )}
        </div>
        {msg && <p className="notice ok">{msg}</p>}
        {error && <p className="notice err">{error}</p>}
        <div className="actions">
          <button className="btn" disabled={busy} onClick={onClose}>关闭</button>
          <button className="btn" disabled={busy} onClick={() => void help()} data-testid="copy-help">联系管理员</button>
          <label className="btn" style={busy ? { opacity: 0.5, pointerEvents: "none" } : undefined} title="换一份更清楚 / 更小的文件，传上来后重新压缩">
            看不清，换一份<input type="file" hidden aria-label={`换一份 ${name}`} accept="image/*,application/pdf" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; void replace(f); }} />
          </label>
          {copy && !file.sizeIssue && (
            <button className="btn btn-primary" disabled={busy} onClick={() => void confirm()} data-testid="copy-confirm">看得清，确认</button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
